import { Paper, Stack, Flex, Text, Button, Grid, Select } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { TransactionField } from './TransactionField';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useTransactionFromDocumentModalContext } from '../TransactionFromDocumentModalContext';

export const ExtractedDataDisplay = () => {
	const {
		analysisResult,
		editedValues,
		isEditMode,
		setIsEditMode,
		updateFieldValue,
		selectedTransactionIndex,
		setSelectedTransactionIndex,
		uploadedFile,
		getSelectedValue,
		updateSelectedIndex,
	} = useTransactionFromDocumentModalContext();
	const currentDocumentName = uploadedFile?.name;
	const tCapManage = useTranslationCapManage();

	if (!analysisResult) {
		return null;
	}

	const hasMultipleTransactions = (analysisResult.data?.length || 0) > 1;
	const transactionOptions =
		analysisResult.data?.map((_, index) => ({
			value: String(index),
			label: tCapManage('transactions.add_from_document.transaction_option_label', {
				number: index + 1,
			}),
		})) || [];

	return (
		<Paper p="lg" withBorder style={{ width: '100%' }}>
			<Stack gap="md">
				<Flex justify="space-between" align="center">
					<div>
						<Text size="H3Thin" fw={600}>
							{tCapManage(
								'transactions.add_from_document.extracted_transaction_data'
							)}
						</Text>
						{currentDocumentName && (
							<Text size="B3Regular" c={defaultTheme.v3.colors.gray[6]} mt="xs">
								{tCapManage('transactions.add_from_document.document_label', {
									name: currentDocumentName,
								})}
							</Text>
						)}
					</div>
					{!analysisResult.error && (
						<Button
							variant={(isEditMode && 'filled') || 'light'}
							size="sm"
							leftSection={
								<Icon
									variant={(isEditMode && 'c-check') || 'edit-pencil'}
									size="md"
								/>
							}
							onClick={() => setIsEditMode(!isEditMode)}
						>
							{isEditMode &&
								tCapManage('transactions.add_from_document.done_editing')}
							{!isEditMode &&
								tCapManage('transactions.add_from_document.edit_values')}
						</Button>
					)}
				</Flex>

				{hasMultipleTransactions && (
					<Select
						label={tCapManage('transactions.add_from_document.select_transaction')}
						placeholder={tCapManage(
							'transactions.add_from_document.choose_transaction'
						)}
						data={transactionOptions}
						value={String(selectedTransactionIndex)}
						onChange={(value) => {
							const index = parseInt(value || '0', 10);
							setSelectedTransactionIndex(index);
						}}
					/>
				)}

				{!hasMultipleTransactions && (
					<Text size="B3Regular" c={defaultTheme.v3.colors.gray[6]}>
						{tCapManage('transactions.add_from_document.only_one_transaction')}
					</Text>
				)}

				{analysisResult.error && (
					<Text size="B3Regular" c={defaultTheme.v3.colors.red[6]}>
						{tCapManage('transactions.add_from_document.error')}:{' '}
						{analysisResult.errorMessage}
					</Text>
				)}
				{!analysisResult.error && analysisResult.data && analysisResult.data.length > 0 && (
					<Grid>
						<Grid.Col span={6}>
							<TransactionField
								label={tCapManage('transactions.add_from_document.fields.seller')}
								fieldData={editedValues.sellers}
								isEditMode={isEditMode}
								onUpdateValue={(value) => updateFieldValue('sellers', value)}
								fieldType="text"
								// Array-specific props
								isArrayField={true}
								arrayValues={editedValues.sellers}
								selectedValue={getSelectedValue('sellers')}
								onSelectOption={(index) => updateSelectedIndex('sellers', index)}
							/>
						</Grid.Col>

						<Grid.Col span={6}>
							<TransactionField
								label={tCapManage('transactions.add_from_document.fields.buyer')}
								fieldData={editedValues.buyers}
								isEditMode={isEditMode}
								onUpdateValue={(value) => updateFieldValue('buyers', value)}
								fieldType="text"
								// Array-specific props
								isArrayField={true}
								arrayValues={editedValues.buyers}
								selectedValue={getSelectedValue('buyers')}
								onSelectOption={(index) => updateSelectedIndex('buyers', index)}
							/>
						</Grid.Col>

						<Grid.Col span={6}>
							<TransactionField
								label={tCapManage(
									'transactions.add_from_document.fields.security_type'
								)}
								fieldData={editedValues.securityType}
								isEditMode={isEditMode}
								onUpdateValue={(value) => updateFieldValue('securityType', value)}
								fieldType="text"
							/>
						</Grid.Col>

						<Grid.Col span={6}>
							<TransactionField
								label={tCapManage(
									'transactions.add_from_document.fields.number_of_units'
								)}
								fieldData={editedValues.numberOfUnits}
								isEditMode={isEditMode}
								onUpdateValue={(value) => updateFieldValue('numberOfUnits', value)}
								fieldType="number"
							/>
						</Grid.Col>

						<Grid.Col span={6}>
							<TransactionField
								label={tCapManage(
									'transactions.add_from_document.fields.total_cost'
								)}
								fieldData={editedValues.totalCost}
								isEditMode={isEditMode}
								onUpdateValue={(value) => updateFieldValue('totalCost', value)}
								fieldType="currency"
							/>
						</Grid.Col>

						<Grid.Col span={6}>
							<TransactionField
								label={tCapManage(
									'transactions.add_from_document.fields.price_per_unit'
								)}
								fieldData={editedValues.pricePerUnit}
								isEditMode={isEditMode}
								onUpdateValue={(value) => updateFieldValue('pricePerUnit', value)}
								fieldType="currency"
							/>
						</Grid.Col>

						<Grid.Col span={12}>
							<TransactionField
								label={tCapManage(
									'transactions.add_from_document.fields.transaction_type'
								)}
								fieldData={editedValues.transactionType}
								isEditMode={isEditMode}
								onUpdateValue={(value) =>
									updateFieldValue('transactionType', value)
								}
								fieldType="text"
							/>
						</Grid.Col>

						<Grid.Col span={6}>
							<TransactionField
								label={tCapManage(
									'transactions.add_from_document.fields.transaction_date'
								)}
								fieldData={editedValues.transactionDate}
								isEditMode={isEditMode}
								onUpdateValue={(value) =>
									updateFieldValue('transactionDate', value)
								}
								fieldType="date"
								placeholder="YYYY-MM-DD"
							/>
						</Grid.Col>
					</Grid>
				)}
				{!analysisResult.error &&
					(!analysisResult.data || analysisResult.data.length === 0) && (
						<Text size="B3Regular" c={defaultTheme.v3.colors.gray[6]}>
							{tCapManage('transactions.add_from_document.no_data_extracted')}
						</Text>
					)}
			</Stack>
		</Paper>
	);
};
